import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatDate, sortDDLByLabel, useNavigateToLogout } from "../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminEarningsService } from "../../services/AdminEarningsService";
import { CountryService } from "../../services/common/CountryService";
import { DDLDataTable } from "../DDLDataTable";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAppState } from "../../store/reducers";
import { IGetListRequest } from "../../models";
import { IPayrollQPIPEarnings } from "../../models";
import { PayrollQPIPEarningService } from "../../services/payrollQPIPEarningService";
import { PayrollQPIPEarningsSetupFieldsPanel } from "./Panels/PayrollQPIPEarningsSetupFieldsPanel";
import { PayrollQPIPEarningsSetupToolbar } from "./PayrollQPIPEarningsSetupToolbar";
import { PayrollQPIPEarningsSetupValidationSchema } from "./validator";
import { RemittanceFrequencyService } from "../../services/RemittanceFrequencyService";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { yupResolver } from "@hookform/resolvers/yup";

// import { payrollQPIPEarningService } from "../../services/payrollQPIPEarningService";

// import { SingleColumnLayout } from "../EmployeeAssignemnt/SingleColumnLayout";

export interface IPayrollQPIPEarningsSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "payrollQPIPEarnings-single-data-record";
const PAYROLL_QPIP_EARNINGS_LIST_DATA_QUERY_KEY = "payrollQPIPEarnings-list";
const useGetData = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: () => {
			return PayrollQPIPEarningService.getById(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const usePayrollQPIPEarnings = (
	clientId: string,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [PAYROLL_QPIP_EARNINGS_LIST_DATA_QUERY_KEY, params],
		queryFn: (data) => {

			return PayrollQPIPEarningService.getEarnings(
				clientId,
				params,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetCountryDDL = () =>
	useQuery({
		queryKey: ["country-ddl"],
		queryFn: () => CountryService.getCountryDDL(),
		refetchOnWindowFocus: false,
	});

const useGetEarningsDDL = () =>
	useQuery({
		queryKey: ["Earnings-ddl"],
		queryFn: () => AdminEarningsService.getEarningsDDL(),
		refetchOnWindowFocus: false,
	});

export const PayrollQPIPEarningsSetup: React.FC<
	IPayrollQPIPEarningsSetup
> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [provinceDDLItems, setProvinceDDLItems] = useState<any>([]);
	const [payrollQPIPEarningsId, setPayrollQPIPEarningsId] = useState<
		number | string
	>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "payrollQPIPEarningName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "payrollQPIPEarningName",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const { isPending, data } = usePayrollQPIPEarnings(
		clientId,
		queryParams,
		navigateToLogout
	);

	const payrollQPIPEarningsList: any | null = data;
	const { isPending: isPendingCountryDDL, data: countryList } =
		useGetCountryDDL();

	const { isPending: isPendingEarningsDDL, data: earningsList } =
		useGetEarningsDDL();

	const {
		isPending: isSubmitting,
		mutate: submitPayrollQPIPEarningsFormFields,
	} = useMutation({
		mutationFn: PayrollQPIPEarningService.create,
		onSuccess: (res: IPayrollQPIPEarnings | IServerError | IResponse | any) => {

			if (res.status === 200) {
				// if (instanceOfIPayrollQPIPEarningsResponse(res)) {
				// setEditRecordId(res.id);
				// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				// }
				let path = `/payroll-qpip-earning/setup/${res.data.id}`;
				navigate(path);
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_QPIP_EARNINGS_LIST_DATA_QUERY_KEY],
					}),
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					}),
				]);
			} else if (res.status === 201) {
				// if (instanceOfIPayrollQPIPEarningsResponse(res)) {
				// setEditRecordId(res.id);
				// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				// }
				let path = `/payroll-qpip-earning/setup/${res.data.id}`;
				navigate(path);
				setEditRecordId(res.data.id);
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_QPIP_EARNINGS_LIST_DATA_QUERY_KEY],
					}),
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					}),
				]);
			} else {
				setSnackbarData({
					open: true,
					message: res.message ? res.message : "Something went wrong!",
					severity: "error",
				});
			}
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		editRecordId,
		navigateToLogout
	);

	const upadtedPayrollQPIPEarningsListItems = useMemo(() => {
		return (
			payrollQPIPEarningsList &&
			payrollQPIPEarningsList.items &&
			payrollQPIPEarningsList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/payroll-qpip-earning/setup/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.payrollQPIPEarningName}
					</Link>
				),
			}))
		);
	}, [payrollQPIPEarningsList]);

	const DEFAULT_VALUES = {
		earningId: undefined,
		province: "Quebec",
		enable: false,
		effectiveDate: null,
		endDate: null,
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(PayrollQPIPEarningsSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const onSubmit = (values: any) => {
		// const selectedProvincesIds = values.province.map((item:any) => item.id)


		// const regenrateProvinceDDLItems = provinceDDLItems.map(
		// 	({ label, value, shortCode }: any) => ({
		// 		name: label,
		// 		id: value,
		// 		shortCode,
		// 	})
		// );


		// const selectedProvinceArrayOfObjects = regenrateProvinceDDLItems.filter(
		// 	(item: any) => values.province.includes(item.id)
		// );



		// const regenrateSelectedProvinceArrayOfObjects =
		// 	selectedProvinceArrayOfObjects.map(({ id, name, shortCode }: any) => ({
		// 		id,
		// 		name,
		// 		shortCode,
		// 		status: values.enable ? 100 : 200,
		// 	}));



		// let finalProvinceList = regenrateProvinceDDLItems.map((item: any) => {
		// 	const match = regenrateSelectedProvinceArrayOfObjects.find(
		// 		(sel: any) => sel.id === item.id
		// 	);
		// 	return match ? match : { ...item, status: 300 };
		// });



		const regenrateProvinceDDLItemsForQC = provinceDDLItems.map(
			({ label, value, shortCode }: any) => ({
				name: label,
				id: value,
				shortCode,
				status: values.enable ? 100 : 200,
			})
		);
		const quebecProvince = regenrateProvinceDDLItemsForQC.filter((x: any) => x.shortCode === 'QC')

		let payload = {
			// earningId: earningId,
			id: id && id,
			earningId: values.earningId,
			navigateToLogout: navigateToLogout,
			province: quebecProvince,
			// province: regenrateSelectedProvinceArrayOfObjects,
			enable: values.enable,
			effectiveDate:
				values.effectiveDate !== null && values.effectiveDate !== ""
					? formatDate(values.effectiveDate)
					: undefined,
			endDate:
				values.endDate && values.endDate !== null && values.endDate !== ""
					? formatDate(values.endDate)
					: undefined,
		};
		submitPayrollQPIPEarningsFormFields(payload);
	};

	useEffect(() => {
		if (id && selectedDataRecord) {

			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "effectiveDate" && fieldValue !== null) {
					formMethods.setValue(fieldName, new Date(fieldValue) ?? undefined);
				} else if (fieldName === "endDate" && fieldValue !== null) {
					formMethods.setValue(fieldName, new Date(fieldValue) ?? undefined);
				} else if (
					fieldName === "province" &&
					fieldValue &&
					fieldValue.length > 0
				) {
					// const selectedProvincesArrayOfObjects = fieldValue
					// 	.filter((x: any) => x.status !== 300)
					// 	.map((x: any) => x.id);
					// // const selectedProvincesArrayOfIds = selectedProvincesArrayOfObjects.map((x:any) => x.id)
					// formMethods.setValue(fieldName, selectedProvincesArrayOfObjects);
					formMethods.setValue(fieldName, 'Quebec');
				}
			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deletePayrollQPIPEarningsById } =
		useMutation({
			mutationFn: PayrollQPIPEarningService.delEarnings,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the PayrollQPIPEarnings fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/payroll-qpip-earning/setup`;
				navigate(path);
				return queryClient.invalidateQueries({
					queryKey: [PAYROLL_QPIP_EARNINGS_LIST_DATA_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deletePayrollQPIPEarningsField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				payrollQPIPEarningsId: payrollQPIPEarningsId,
				navigateToLogout: navigateToLogout,
			};
			deletePayrollQPIPEarningsById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setPayrollQPIPEarningsId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setPayrollQPIPEarningsId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archivePayrollQPIPEarningsById } =
		useMutation({
			mutationFn: PayrollQPIPEarningService.archiveEarnings,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the PayrollQPIPEarnings fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/payroll-qpip-earning/setup`;
				navigate(path);
				return queryClient.invalidateQueries({
					queryKey: [PAYROLL_QPIP_EARNINGS_LIST_DATA_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const archivePayrollQPIPEarningsField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				payrollQPIPEarningsId: payrollQPIPEarningsId,
				navigateToLogout: navigateToLogout,
			};
			archivePayrollQPIPEarningsById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setPayrollQPIPEarningsId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		let path = `/payroll-qpip-earning/setup`;
		navigate(path);
	};

	const expandedLabels = ["Earnings", "QPIP"];
	const expandedQPIPTaxableSetupOrSetupItem = "Setup"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "QPIP" &&
				item.label === expandedQPIPTaxableSetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: parentLabel === "QPIP" && item.label === expandedQPIPTaxableSetupOrSetupItem && "/payroll-qpip-earning/setup",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
		// 		payrollQPIPEarningsName: "",
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const onRowEditClicked = useCallback((params: IPayrollQPIPEarnings | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/payroll-qpip-earning/setup/${params.value}`;
		navigate(path);
	}, []);

	return (
		<Grid
		// sx={{
		// 	pb: "5rem",
		// 	"& .MuiAppBar-root": {
		// 		zIndex: 2,
		// 		top: subMenuVisibility ? "154px" : "112px",
		// 	},
		// }}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingById || isDeleting || isArchive || isPending || isPendingCountryDDL || isPendingEarningsDDL}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deletePayrollQPIPEarningsField}
				cancelActionHandler={onCloseHandler}
				testId="payrollQPIPEarnings-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="PayrollQPIPEarnings"
										testId={`payrollQPIPEarnings`}
										// leftSpacing={1}
										// rightSpacing={1}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<PayrollQPIPEarningsSetupFieldsPanel
											countryList={countryList ?? []}
											earningsList={earningsList && sortDDLByLabel(earningsList ?? [])}
											provinceDDLItems={(value: any) =>
												setProvinceDDLItems(value)
											}
										/>
									</StandardPanel>
								}
								toolbar={
									<PayrollQPIPEarningsSetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								showFullDiaplay={true}
								toolbarPosition="bottom"
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 5, lg: 5, xl: 5 }}></Grid>
						{/* <Grid
							size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}
							sx={{ marginTop: "16px" }}
						>
							<DDLDataTable
								rows={
									payrollQPIPEarningsList && upadtedPayrollQPIPEarningsListItems
										? upadtedPayrollQPIPEarningsListItems
										: []
								}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								isLoading={isPending}
								totalRecords={
									payrollQPIPEarningsList && payrollQPIPEarningsList.total
										? payrollQPIPEarningsList.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
							/>
						</Grid> */}
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			{/* <EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archivePayrollQPIPEarningsField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/> */}

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"payrollQPIPEarnings-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
// function useGetCountryDDL(): { isPending: any; data: any; } {
// 	throw new Error("Function not implemented.");
// }
